import { ReachAPI } from '../ReachApi';
import {
    HandlebarsTemplate,
    VueScheduleTemplate,
    VueSocialmediaTemplate,
    VueContentTemplate,
    VueWeatherTemplate,
    VueQuoteTemplate,
    TemplateConfiguration,
    VueListTemplate,
    TemplateEngine,
} from '.';
import { objectHasOwnProperty } from '../Utility';
import { GenericAbstractConstructor } from '../Utility/Mixin';
import { GenericWidgetConfigurationDetails, Widget } from '../Widget';
import { ScheduleWidgetConfigurationDetails } from '../Widget/ScheduleWidget/types';
import { Log } from '../AuxillaryService/Log';
import { HandlebarsTemplateConfiguration } from './Handlebars/types';
import { VueTemplateConfiguration, VueTemplateVersionInfo } from './Vue';

/**
 * Regardless of engine type, it must compile an object with a render function
 * that accepts context
 */
type RenderableTemplate = { render(context: unknown): void };

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function TemplateWidgetMixin<TBase extends GenericAbstractConstructor<Widget>>(Base: TBase) {
    abstract class TemplateWidgetMixin extends Base {
        /**
         * Engine lookup for template class
         */
        private static templatingEngines = {
            handlebars: HandlebarsTemplate,
            vue: {
                //Add template reference here
                schedule: VueScheduleTemplate,
                medallion: VueSocialmediaTemplate,
                content: VueContentTemplate,
                weather: VueWeatherTemplate,
                quote: VueQuoteTemplate,
                list: VueListTemplate,
            },
        };

        /**
         * Cached compiled Template
         */
        #template: RenderableTemplate | null = null;

        /**
         * Public readonly access to cached template
         */
        get template(): RenderableTemplate | null {
            return this.#template;
        }

        /**
         * Cached template configuration, stored to prevent needless calls to the API
         */
        #templateConfiguration: TemplateConfiguration | null = null;

        get templateConfiguration(): TemplateConfiguration | null {
            return this.#templateConfiguration;
        }

        /**
         * Retrieve template(s) from API and sends them to handler to compile Template
         * If the template has already been successfully compiled, return cached copy
         */
        private async getTemplate(): Promise<RenderableTemplate> {
            const templateId = this.config.template || '';

            try {
                if (!this.#templateConfiguration) {
                    const templateDataResponse = await ReachAPI.deferredToPromise(
                        ReachAPI.display.getTemplateCombined(this.type, templateId)
                    );
                    this.#templateConfiguration = templateDataResponse.data
                        .template as TemplateConfiguration;
                }

                return this.onTemplateDataFetched(this.#templateConfiguration);
            } catch (error) {
                throw new Error('Failed to retrieve template(s) associated with this widget');
            }
        }

        /**
         * Accepts a TemplateConfiguration and builds a RenderableTemplate
         */
        private onTemplateDataFetched(templateData: TemplateConfiguration): RenderableTemplate {
            if (templateData.framework === TemplateEngine.VUE) {
                this.#template = this.buildVueTemplate(templateData as VueTemplateConfiguration);
            } else {
                this.#template = this.buildHandlebarsTemplate(
                    templateData as HandlebarsTemplateConfiguration
                );
            }

            return this.#template;
        }

        /**
         * Builds a RenderableTemplate using the Vue engine
         */
        private buildVueTemplate(vueTemplateData: VueTemplateConfiguration): RenderableTemplate {
            const templateInfo = this.getVueTemplateInfo(vueTemplateData);

            const customCss = this.getCustomCss();
            if (customCss) {
                templateInfo.css = customCss;
            }

            return new TemplateWidgetMixin.templatingEngines[vueTemplateData.framework][
                vueTemplateData.type
            ](templateInfo, this.wrapperElement);
        }

        /**
         * Builds a RenderableTemplate using the Handlebars engine
         * @todo: document how the different ways the css can be applied
         */
        private buildHandlebarsTemplate(
            handlebarsTemplateData: HandlebarsTemplateConfiguration
        ): RenderableTemplate {
            const customCss = this.getCustomCss();

            return new TemplateWidgetMixin.templatingEngines[handlebarsTemplateData.framework](
                {
                    id: handlebarsTemplateData.id,
                    type: handlebarsTemplateData.type,
                    html: handlebarsTemplateData.content,
                    css: customCss || handlebarsTemplateData.css,
                    js: null,
                },
                this.wrapperElement
            );
        }

        private getCustomCss(): string | null {
            if (
                this.cssTemplates &&
                this.config.template &&
                objectHasOwnProperty(this.cssTemplates, this.config.template)
            ) {
                const templateIndex = parseInt(this.config.template);
                const maybeCustomCss = this.cssTemplates[templateIndex];
                if (maybeCustomCss) {
                    return maybeCustomCss;
                }
            }

            return this.config.css_template;
        }

        /**
         * Determines which version of a template the widget should use
         * and returns the specific configuration for that version
         */
        private getVueTemplateInfo(templateData: VueTemplateConfiguration): VueTemplateVersionInfo {
            let requiredVersion: number;

            if (TemplateWidgetMixin.isVersionable(this.config)) {
                requiredVersion = this.config.template_version
                    ? parseInt(this.config.template_version)
                    : parseInt(templateData.default_version);
            } else {
                requiredVersion = templateData.default_version
                    ? parseInt(templateData.default_version)
                    : 1;
            }

            const maybeTemplate = templateData.versions.find((item) => {
                return parseInt(item.version) === requiredVersion;
            });

            if (!maybeTemplate) {
                throw new Error(`No Vue template found for version ${requiredVersion}`);
            }

            return maybeTemplate;
        }

        /**
         * Widgets using this mixin must provide an implementation to this method
         */
        protected abstract getRenderContext(): Record<string, unknown>;

        /**
         * Render content to the DOM
         */
        protected async renderContent(): Promise<void>;
        /**
         * @deprecated - Use renderContext without providing the context.  Doing so will ensure
         * a firm contract is upheld for the data a widget must supply to its templates.
         */
        protected async renderContent(context: Record<string, unknown>): Promise<void>;
        protected async renderContent(context?: Record<string, unknown>) {
            try {
                const template = await this.getTemplate();
                context = context || this.getRenderContext();
                return template.render(context);
            } catch (error) {
                Log.Error(`Template render failed`, null, __fileline, error);
                throw error;
            }
        }

        //TODO: What is the correct way to handle this assertion?  Should a new generic config type
        // be made?  Perhaps a union of all configs that can be versioned?  Either way, hard
        // casting this to ScheduleWidgetConfiguration is not a good solution.
        private static isVersionable(
            config: GenericWidgetConfigurationDetails
        ): config is ScheduleWidgetConfigurationDetails {
            return 'template_version' in config;
        }
    }

    return TemplateWidgetMixin;
}
